/**
 * Text Management Module
 */
import Konva from "konva";
import { getCurrentFontFamily, isMobile } from "./utils.js";

export class TextManager {
	constructor(stageManager, appState) {
		this.stageManager = stageManager;
		this.appState = appState;
		this.$textConfigPanel = jQuery(".mc-text-config-panel");
		this.$colorOptionsContainer = jQuery(".mc-text-color-options");
		this.$colorOptions = jQuery();
		this.colorConfigs = [];
		this.defaultShadowConfig = {
			color: "black",
			opacity: 0.7,
			blur: 4,
			offsetX: 0,
			offsetY: 0,
		};
		this.defaultColorConfig = {
			id: "white",
			label: "Iškaltos",
			fill: "#ffffff",
			opacity: 0.7,
			shadow: {
				color: "#ffffff",
				opacity: 0.8,
				blur: 8,
				offsetX: 0,
				offsetY: 0,
			},
		};
		this.refreshColorOptions();
		this.selectedColorConfig = this.getInitialColorConfig();
	}

	/**
	 * Create a new text node
	 */
	createTextNode(textValue, centerX, y, fontSize) {
		const stage = this.stageManager.getStage();
		const mainLayer = this.stageManager.getMainLayer();
		const fontFamily = getCurrentFontFamily();
		const colorConfig = this.getActiveColorConfig();

		const defaultShadow = this.defaultShadowConfig;
		const text = new Konva.Text({
			id: "t" + this.appState.incrementTextId(),
			text: textValue || window.MC_DATA.i18n.text,
			fontFamily: fontFamily,
			fontSize: fontSize || 15,
			fill: colorConfig.fill,
			opacity: this.normalizeOpacity(colorConfig.opacity),
			align: "center",
			padding: 10,
			wrap: "word",
			draggable: true,
			x: 0,
			y: y || stage.height() / 2,
			name: "mc-text",
			shadowColor: defaultShadow.color,
			shadowBlur: defaultShadow.blur,
			shadowOpacity: defaultShadow.opacity,
			shadowOffsetX: defaultShadow.offsetX,
			shadowOffsetY: defaultShadow.offsetY,
		});

		this.applyColorToNode(text, colorConfig);

		mainLayer.add(text);

		// Center the text
		const width = text.width();
		const cx = centerX || stage.width() / 2;
		text.x(cx - width / 2);
		this.normalizeTextNodeBounds(text);

		this.bindTextNodeInteractions(text);
		mainLayer.draw();

		return text;
	}

	normalizeTextNodeBounds(node) {
		if (!node) return;

		// Reset height to auto (undefined) so Konva calculates it based on content and width
		node.height(undefined);

		// Force transformer update if this node is selected
		const transformer = this.stageManager.getTransformer();
		if (transformer && transformer.nodes().indexOf(node) > -1) {
			transformer.forceUpdate();
		}
	}

	bindTextNodeInteractions(node) {
		if (!node || typeof node.on !== "function") return;
		this.normalizeTextNodeBounds(node);

		node.off(".mcTextInteractions");

		node.on("transform.mcTextInteractions", () => {
			// Update width based on transformer
			const nextWidth = node.width() * node.scaleX();
			node.width(nextWidth);
			node.scaleX(1);
			node.scaleY(1);

			// Auto-update height based on new width
			this.normalizeTextNodeBounds(node);
		});

		const handleSelect = (evt) => {
			if (window.mcSetActiveTextElement) {
				window.mcSetActiveTextElement(node, {
					multiSelect: this.isMultiSelectGesture(evt),
					autoOpenConfig: !isMobile(),
					originalEvent: evt && evt.evt ? evt.evt : evt,
				});
			}
		};

		node.on(
			"click.mcTextInteractions tap.mcTextInteractions",
			handleSelect
		);
		node.on("dragstart.mcTextInteractions", () => {
			jQuery(".mc-text-toolbar").hide();
		});
		node.on("dragend.mcTextInteractions", handleSelect);
	}

	/**
	 * Set active text node
	 */
	setActiveText(node) {
		this.appState.setActiveText(node);
		const transformer = this.stageManager.getTransformer();
		const mainLayer = this.stageManager.getMainLayer();

		if (node) {
			this.stageManager.setPhotosNotActive();
			transformer.nodes([node]);
			transformer.enabledAnchors(["middle-left", "middle-right"]);
			transformer.rotateEnabled(true);
			transformer.keepRatio(false);
			mainLayer.draw();

			this.syncColorSelectionFromNode(node);
		} else {
			transformer.nodes([]);
			mainLayer.draw();
		}
	}

	isMultiSelectGesture(evt) {
		const nativeEvent = evt && evt.evt ? evt.evt : evt;
		if (!nativeEvent) return false;
		return Boolean(
			nativeEvent.shiftKey || nativeEvent.metaKey || nativeEvent.ctrlKey
		);
	}

	/**
	 * Open text configuration panel
	 */
	openTextConfig() {
		const activeTextNode = this.appState.activeTextNode;
		if (!activeTextNode) return;

		jQuery(".mc-font-family").val(activeTextNode.fontFamily());
		jQuery(".mc-text-input").val(activeTextNode.text());
		jQuery(".mc-font-size-range").val(activeTextNode.fontSize());

		const align = activeTextNode.align();
		jQuery(".mc-align-btn").removeClass("mc-align-active");
		jQuery('.mc-align-btn[data-align="' + align + '"]').addClass(
			"mc-align-active"
		);
		this.syncColorSelectionFromNode(activeTextNode);

		this.$textConfigPanel.fadeIn(150);
	}

	/**
	 * Close text configuration panel
	 */
	closeTextConfig() {
		this.$textConfigPanel.fadeOut(150);
	}

	/**
	 * Add default texts to the stage
	 */
	addDefaultTexts() {
		const stage = this.stageManager.getStage();
		const mainLayer = this.stageManager.getMainLayer();

		// Check if texts already exist
		const existing = mainLayer.find(".mc-text");
		if (existing.length > 0) return;

		const cx = stage.width() / 2;
		const cy = stage.height() / 2;
		const gap = 40;

		this.createTextNode(
			window.MC_DATA.i18n.defaultName,
			cx,
			cy - gap * 0.2,
			20
		);
		this.createTextNode(
			window.MC_DATA.i18n.defaultDates,
			cx,
			cy + gap * 0.7,
			16
		);
		this.createTextNode(
			window.MC_DATA.i18n.defaultEpitaph,
			cx,
			cy + gap * 1.6,
			18
		);
	}

	/**
	 * Delete active text node
	 */
	deleteActiveText() {
		const activeTextNode = this.appState.activeTextNode;
		if (!activeTextNode) return;

		activeTextNode.destroy();
		this.setActiveText(null);
		this.closeTextConfig();
		if (window.mcSetActiveTextElement) {
			window.mcSetActiveTextElement(null);
		}

		const mainLayer = this.stageManager.getMainLayer();
		mainLayer.draw();
	}

	/**
	 * Duplicate active text node
	 */
	duplicateActiveText() {
		const activeTextNode = this.appState.activeTextNode;
		if (!activeTextNode) return;

		const clone = activeTextNode.clone({
			x: activeTextNode.x() + 10,
			y: activeTextNode.y() + 10,
			id: "t" + this.appState.incrementTextId(),
		});

		const mainLayer = this.stageManager.getMainLayer();
		mainLayer.add(clone);
		this.normalizeTextNodeBounds(clone);
		this.bindTextNodeInteractions(clone);
		mainLayer.draw();

		this.setActiveText(clone);
		this.openTextConfig();
		if (window.mcSetActiveTextElement) {
			window.mcSetActiveTextElement(clone, {
				multiSelect: false,
				autoOpenConfig: !isMobile(),
			});
		}
	}

	/**
	 * Update text content
	 */
	updateTextContent(value) {
		const activeTextNode = this.appState.activeTextNode;
		if (!activeTextNode) return;

		activeTextNode.text(value);
		this.normalizeTextNodeBounds(activeTextNode);
		const mainLayer = this.stageManager.getMainLayer();
		mainLayer.batchDraw();
	}

	/**
	 * Update text font family
	 */
	updateFontFamily(fontFamily) {
		const activeTextNode = this.appState.activeTextNode;
		if (!activeTextNode) return;

		activeTextNode.fontFamily(fontFamily);
		this.normalizeTextNodeBounds(activeTextNode);
		const mainLayer = this.stageManager.getMainLayer();
		mainLayer.batchDraw();
	}

	/**
	 * Update text font size
	 */
	updateFontSize(size) {
		const activeTextNode = this.appState.activeTextNode;
		if (!activeTextNode) return;

		activeTextNode.fontSize(size);
		this.normalizeTextNodeBounds(activeTextNode);
		const mainLayer = this.stageManager.getMainLayer();
		mainLayer.batchDraw();
	}

	/**
	 * Update text alignment
	 */
	updateAlignment(align) {
		const activeTextNode = this.appState.activeTextNode;
		if (!activeTextNode) return;

		activeTextNode.align(align);
		const mainLayer = this.stageManager.getMainLayer();
		mainLayer.batchDraw();
	}

	/**
	 * Re-apply current font family to force Konva redraw after webfonts load
	 */
	refreshAllTextFonts() {
		const mainLayer = this.stageManager.getMainLayer();
		const textNodes = mainLayer.find(".mc-text");
		if (!textNodes.length) {
			return;
		}

		textNodes.forEach((node) => {
			const currentFont = node.fontFamily();
			node.fontFamily(currentFont);
			this.normalizeTextNodeBounds(node);
		});

		mainLayer.batchDraw();
	}

	/**
	 * Normalize opacity values
	 */
	normalizeOpacity(value, fallback = 1) {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : fallback;
	}

	/**
	 * Refresh cached color option elements and configs
	 */
	refreshColorOptions() {
		this.$colorOptions = jQuery(".mc-text-color-option");
		this.colorConfigs = this.$colorOptions
			.map((_, el) => this.extractColorConfig(jQuery(el)))
			.get()
			.filter(Boolean);
	}

	/**
	 * Extract config info from a color option element
	 */
	extractColorConfig($option) {
		if (!$option || !$option.length) {
			return null;
		}

		const fill = ($option.data("fill") || "").toString();
		if (!fill) {
			return null;
		}

		const opacityData = $option.data("opacity");
		const opacity = Number.isFinite(opacityData)
			? Number(opacityData)
			: this.defaultColorConfig.opacity;

		return {
			id: ($option.data("color") || fill).toString(),
			label: ($option.data("label") || $option.text() || "").trim(),
			fill,
			opacity,
			shadow: this.extractShadowConfig($option),
		};
	}

	getInitialColorConfig() {
		if (!this.$colorOptions || !this.$colorOptions.length) {
			return this.defaultColorConfig;
		}

		const $active = this.$colorOptions.filter(".is-active").first();
		const activeConfig = this.extractColorConfig($active);
		return activeConfig || this.colorConfigs[0] || this.defaultColorConfig;
	}

	getActiveColorConfig() {
		return this.selectedColorConfig || this.getInitialColorConfig();
	}

	getColorConfigById(id) {
		if (!id) return null;
		return this.colorConfigs.find((config) => config.id === id) || null;
	}

	/**
	 * Extract optional shadow configuration from data attributes
	 */
	extractShadowConfig($option) {
		if (!$option || !$option.length) {
			return null;
		}

		const readAttr = (attr) => $option.attr(attr);
		const colorValue = readAttr("data-shadow-color");
		const opacityValue = readAttr("data-shadow-opacity");
		const blurValue = readAttr("data-shadow-blur");
		const offsetXValue = readAttr("data-shadow-offset-x");
		const offsetYValue = readAttr("data-shadow-offset-y");

		const hasValue =
			colorValue !== undefined ||
			opacityValue !== undefined ||
			blurValue !== undefined ||
			offsetXValue !== undefined ||
			offsetYValue !== undefined;

		if (!hasValue) {
			return null;
		}

		const parseNumber = (value) => {
			const parsed = Number(value);
			return Number.isFinite(parsed) ? parsed : null;
		};

		return {
			color: colorValue ? colorValue.toString() : null,
			opacity: parseNumber(opacityValue),
			blur: parseNumber(blurValue),
			offsetX: parseNumber(offsetXValue),
			offsetY: parseNumber(offsetYValue),
		};
	}

	/**
	 * Normalize incoming shadow config by merging defaults
	 */
	normalizeShadowConfig(shadow) {
		const fallback = this.defaultShadowConfig;
		if (!shadow) {
			return { ...fallback };
		}

		const parseOrFallback = (value, fallbackValue) => {
			const parsed = Number(value);
			return Number.isFinite(parsed) ? parsed : fallbackValue;
		};

		return {
			color: shadow.color || fallback.color,
			opacity: this.normalizeOpacity(shadow.opacity, fallback.opacity),
			blur: parseOrFallback(shadow.blur, fallback.blur),
			offsetX: parseOrFallback(shadow.offsetX, fallback.offsetX),
			offsetY: parseOrFallback(shadow.offsetY, fallback.offsetY),
		};
	}

	/**
	 * Read persisted shadow data from a Konva node
	 */
	extractShadowFromNode(node) {
		if (!node) {
			return { ...this.defaultShadowConfig };
		}

		const readAttr = (attrName, fallbackValue) => {
			const attrValue = node.getAttr(attrName);
			return attrValue !== undefined ? attrValue : fallbackValue;
		};

		return this.normalizeShadowConfig({
			color: readAttr("mcShadowColor", node.shadowColor()),
			opacity: readAttr(
				"mcShadowOpacity",
				this.normalizeOpacity(node.shadowOpacity())
			),
			blur: readAttr("mcShadowBlur", node.shadowBlur()),
			offsetX: readAttr("mcShadowOffsetX", node.shadowOffsetX()),
			offsetY: readAttr("mcShadowOffsetY", node.shadowOffsetY()),
		});
	}

	matchColorConfig(fill, opacity) {
		if (!fill) return null;
		const normalizedFill = fill.toString().toLowerCase();
		const normalizedOpacity = this.normalizeOpacity(opacity);
		const exactMatch = this.colorConfigs.find((config) => {
			return (
				config.fill.toLowerCase() === normalizedFill &&
				Math.abs(
					this.normalizeOpacity(config.opacity) - normalizedOpacity
				) < 0.01
			);
		});
		if (exactMatch) {
			return exactMatch;
		}

		return (
			this.colorConfigs.find(
				(config) => config.fill.toLowerCase() === normalizedFill
			) || null
		);
	}

	getColorConfigFromNode(node) {
		if (!node) return null;
		const colorId = node.getAttr("mcColorId");
		if (colorId) {
			return (
				this.getColorConfigById(colorId) || {
					id: colorId,
					fill: node.getAttr("mcColorFill") || node.fill(),
					label: node.getAttr("mcColorLabel") || colorId,
					opacity:
						node.getAttr("mcColorOpacity") ??
						this.normalizeOpacity(node.opacity()),
					shadow: this.extractShadowFromNode(node),
				}
			);
		}

		return this.matchColorConfig(node.fill(), node.opacity());
	}

	syncColorSelectionFromNode(node) {
		if (!node) return;
		const config =
			this.getColorConfigFromNode(node) || this.getActiveColorConfig();
		if (!config) return;
		this.selectedColorConfig = config;
		this.highlightColorOption(config.id);
	}

	highlightColorOption(colorId) {
		if (!this.$colorOptions || !this.$colorOptions.length) {
			this.refreshColorOptions();
		}

		if (!this.$colorOptions.length) {
			return;
		}

		let $target = jQuery();
		if (colorId) {
			$target = this.$colorOptions.filter(
				(_, el) => jQuery(el).data("color") === colorId
			);
		}

		if (!$target.length) {
			$target = this.$colorOptions.first();
		}

		this.$colorOptions.removeClass("is-active");
		const $final = $target.first();
		$final.addClass("is-active");
		const resolvedConfig = this.extractColorConfig($final);
		if (resolvedConfig) {
			this.selectedColorConfig = resolvedConfig;
		}
	}

	selectColorOption($option) {
		const config = this.extractColorConfig($option);
		if (!config) return;

		this.refreshColorOptions();
		this.selectedColorConfig = config;
		this.$colorOptions.removeClass("is-active");
		$option.addClass("is-active");
		this.applyColorToActiveText(config);
	}

	applyColorToActiveText(config) {
		if (!this.appState.activeTextNode) return;
		this.applyColorToNode(this.appState.activeTextNode, config);
		const mainLayer = this.stageManager.getMainLayer();
		mainLayer.batchDraw();
	}

	applyColorToNode(node, colorConfig) {
		if (!node || !colorConfig) return;
		const opacity = this.normalizeOpacity(colorConfig.opacity);
		node.fill(colorConfig.fill);
		node.opacity(opacity);
		node.setAttr("mcColorId", colorConfig.id);
		node.setAttr("mcColorFill", colorConfig.fill);
		node.setAttr("mcColorOpacity", opacity);
		node.setAttr(
			"mcColorLabel",
			(colorConfig.label || colorConfig.id || "").toString()
		);
		const shadowConfig = this.normalizeShadowConfig(colorConfig.shadow);
		node.shadowColor(shadowConfig.color);
		node.shadowOpacity(this.normalizeOpacity(shadowConfig.opacity));
		node.shadowBlur(shadowConfig.blur);
		node.shadowOffsetX(shadowConfig.offsetX);
		node.shadowOffsetY(shadowConfig.offsetY);
		node.setAttr("mcShadowColor", shadowConfig.color);
		node.setAttr("mcShadowOpacity", shadowConfig.opacity);
		node.setAttr("mcShadowBlur", shadowConfig.blur);
		node.setAttr("mcShadowOffsetX", shadowConfig.offsetX);
		node.setAttr("mcShadowOffsetY", shadowConfig.offsetY);
	}
}
